import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import MoneyInput from '../components/MoneyInput';
import { fmtDate } from '../utils/date';
import { PageError, loadError } from '../components/OfflineNotice';

function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 999,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '14px', padding: '1.5rem',
        width: '100%', maxWidth: '360px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1rem' }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function UpgradeWall() {
  const navigate = useNavigate();
  return (
    <div className="page">
      <BackButton />
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💰</div>
        <h2 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Enterprise Feature</h2>
        <p style={{ color: 'var(--color-muted)', maxWidth: '320px', margin: '0 auto 1.5rem' }}>
          Fund tracking and capital management are available on the Enterprise plan.
        </p>
        <button
          onClick={() => navigate('/upgrade')}
          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', border:'none', borderRadius:'12px', padding:'1.25rem 2rem', fontWeight:700, fontSize:'1rem', cursor:'pointer', marginTop:'0.5rem' }}
        >
          View Enterprise plan →
        </button>
      </div>
    </div>
  );
}

export default function FundTracker() {
  const { parentCompany, updateParentCompany, isEnterprise } = useAuth();
  const [fund, setFund]       = useState(null);
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ amount: '', note: '' });
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [showCapitalModal, setShowCapitalModal] = useState(false);
  const [capitalInput, setCapitalInput]         = useState('');
  const [savingCapital, setSavingCapital]       = useState(false);
  const [selectedEvent, setSelectedEvent]       = useState(null);
  const [showCapitalDetail, setShowCapitalDetail] = useState(false);

  const load = () => {
    Promise.all([companyAPI.fundBalance(), companyAPI.listCapital()])
      .then(([bRes, eRes]) => {
        setFund(bRes.data.fund);
        setEvents(eRes.data.events || []);
      })
      .catch((err) => setError(err.response?.data?.mixed_currency ? err.response.data.error : loadError(err, 'Failed to load fund data')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);


  const cur = parentCompany?.currency || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!navigator.onLine) { setFormError('Capital injections require an internet connection'); return; }
    const amount = parseFloat(form.amount);
    if (!amount || amount === 0) { setFormError('Enter a valid amount (positive or negative)'); return; }
    setSaving(true);
    setFormError('');
    try {
      await companyAPI.addCapital({ amount, note: form.note });
      setForm({ amount: '', note: '' });
      setShowForm(false);
      setLoading(true);
      load();
      updateParentCompany({});
    } catch {
      setFormError('Failed to add capital event');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCapital = async () => {
    const val = parseFloat(capitalInput);
    if (isNaN(val) || val < 0) return;
    setSavingCapital(true);
    try {
      await companyAPI.setInitialCapital({ initial_capital: val });
      updateParentCompany({ initial_capital: val });
      setShowCapitalModal(false);
      load();
    } catch { /* ignore */ }
    finally { setSavingCapital(false); }
  };

  const handleVoidCapital = async () => {
    try {
      await companyAPI.setInitialCapital({ initial_capital: 0 });
      updateParentCompany({ initial_capital: 0 });
      load();
    } catch { /* ignore */ }
  };

  const handleVoid = async (id) => {
    if (!navigator.onLine) { alert('Requires internet connection'); return; }
    if (!window.confirm('Void this capital event?')) return;
    try {
      await companyAPI.voidCapital(id);
      setLoading(true);
      load();
    } catch { alert('Failed to void event'); }
  };

  if (!isEnterprise) return <UpgradeWall />;
  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><PageError error={error} /></div>;

  const balance = Number(fund?.balance) || 0;

  return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Fund Tracker</h1>
      <p className="text-muted" style={{ marginBottom: '1rem' }}>{cur} · Capital &amp; cash flow</p>

      {/* Balance hero */}
      <div className="card" style={{ marginBottom: '1rem', textAlign: 'center', padding: '1.5rem 1rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
          Available Balance
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 700, color: balance >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {cur} {fmt(balance)}
        </div>
      </div>

      {/* Fund breakdown */}
      <h2 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
        Breakdown
      </h2>

      {/* Initial Capital — clickable to edit, voidable */}
      <div
        className="card"
        style={{ marginBottom: '0.4rem', cursor: 'pointer' }}
        onClick={() => { setCapitalInput(String(fund?.initial_capital || 0)); setShowCapitalModal(true); }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.9rem' }}>Initial Capital</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginTop: '0.1rem' }}>
              {fund?.company_created_at ? fmtDate(fund.company_created_at) : '—'}
            </div>
          </div>
          <span>{cur} {fmt(fund?.initial_capital)}</span>
        </div>
      </div>

      {/* Injections — tap hint pointing to + Add below */}
      <div
        className="card"
        onClick={() => setShowForm(true)}
        style={{ marginBottom: '0.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      >
        <span style={{ fontSize: '0.9rem' }}>+ Injections</span>
        <span>{cur} {fmt(fund?.total_injections)}</span>
      </div>

      {[
        ['+ Cash Collected',  fund?.cash_collected,    false],
        ['− Purchases',       fund?.purchase_outflows, true],
        ['− Expenses',        fund?.expense_outflows,  true],
        ['− Assets bought',   fund?.asset_outflows,    true],
      ].map(([label, val, negative]) => (
        <div key={label} className="card" style={{ marginBottom: '0.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'var(--color-muted)', fontSize: '0.9rem' }}>{label}</span>
          <span style={{ color: negative ? 'var(--color-danger)' : 'inherit' }}>
            {negative ? '−' : ''}{cur} {fmt(val)}
          </span>
        </div>
      ))}

      {/* Capital injection button */}
      <div style={{ margin: '1.5rem 0 0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', margin: 0 }}>Capital Events</h2>
        <button
          onClick={() => setShowForm(s => !s)}
          style={{
            padding: '0.35rem 0.85rem',
            fontSize: '0.8rem',
            fontWeight: 600,
            background: showForm ? 'transparent' : 'var(--color-primary)',
            color: showForm ? 'var(--color-muted)' : '#fff',
            border: showForm ? '1px solid var(--color-border)' : 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {!navigator.onLine && (
        <p style={{ fontSize: '0.8rem', color: 'var(--color-warning)', marginBottom: '0.5rem' }}>
          Capital events require an internet connection.
        </p>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>Amount ({cur}) — positive to inject, negative to withdraw</label>
            <MoneyInput
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="e.g. 500 or -200"
              style={{ width: '100%', marginTop: '0.25rem' }}
              required
            />
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>Note (optional)</label>
            <input
              type="text"
              value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="e.g. Monthly top-up"
              style={{ width: '100%', marginTop: '0.25rem' }}
            />
          </div>
          {formError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: 0 }}>{formError}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Record Event'}
          </button>
        </form>
      )}

      {/* Events list — initial capital shown as first entry */}
      {events.length === 0 && Number(fund?.initial_capital) === 0 && (
        <p className="text-muted" style={{ fontSize: '0.9rem' }}>No capital events yet.</p>
      )}

      {Number(fund?.initial_capital) > 0 && (
        <div
          className="card"
          onClick={() => setShowCapitalDetail(true)}
          style={{ marginBottom: '0.5rem', cursor: 'pointer' }}
        >
          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--color-success)' }}>
            +{cur} {fmt(fund.initial_capital)}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)', marginTop: '0.15rem' }}>
            {fund?.company_created_at ? fmtDate(fund.company_created_at) : '—'} · Initial capital
          </div>
        </div>
      )}

      {events.map(ev => (
        <div
          key={ev.id}
          className="card"
          onClick={() => setSelectedEvent(ev)}
          style={{ marginBottom: '0.5rem', cursor: 'pointer' }}
        >
          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: Number(ev.amount) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {Number(ev.amount) >= 0 ? '+' : ''}{cur} {fmt(ev.amount)}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)', marginTop: '0.15rem' }}>
            {fmtDate(ev.event_at)}{ev.note ? ` · ${ev.note}` : ''}
          </div>
        </div>
      ))}

      {/* Initial capital detail modal */}
      {showCapitalDetail && (
        <Modal title="Initial Capital" onClose={() => setShowCapitalDetail(false)}>
          <div style={{ marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Amount</span>
              <span style={{ fontWeight: 700, color: 'var(--color-success)' }}>+{cur} {fmt(fund?.initial_capital)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Date</span>
              <span style={{ fontSize: '0.85rem' }}>{fund?.company_created_at ? fmtDate(fund.company_created_at) : '—'}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => { handleVoidCapital(); setShowCapitalDetail(false); }}
              style={{ flex: 1, padding: '0.55rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', color: 'var(--color-danger)', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}
            >
              Void entry
            </button>
            <button
              onClick={() => setShowCapitalDetail(false)}
              style={{ flex: 1, padding: '0.55rem', background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Capital event detail modal */}
      {selectedEvent && (
        <Modal title="Capital Event" onClose={() => setSelectedEvent(null)}>
          <div style={{ marginBottom: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Amount</span>
              <span style={{ fontWeight: 700, color: Number(selectedEvent.amount) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                {Number(selectedEvent.amount) >= 0 ? '+' : ''}{cur} {fmt(selectedEvent.amount)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Date</span>
              <span style={{ fontSize: '0.85rem' }}>{fmtDate(selectedEvent.event_at)}</span>
            </div>
            {selectedEvent.note && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>Note</span>
                <span style={{ fontSize: '0.85rem' }}>{selectedEvent.note}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => { handleVoid(selectedEvent.id); setSelectedEvent(null); }}
              style={{ flex: 1, padding: '0.55rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', color: 'var(--color-danger)', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}
            >
              Void entry
            </button>
            <button
              onClick={() => setSelectedEvent(null)}
              style={{ flex: 1, padding: '0.55rem', background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Initial Capital modal */}
      {showCapitalModal && (
        <Modal title="Set Initial Capital" onClose={() => setShowCapitalModal(false)}>
          <p style={{ fontSize: '0.82rem', color: 'var(--color-muted)', marginBottom: '0.75rem' }}>
            This is the starting capital your company was funded with before any sales or injections.
          </p>
          <MoneyInput
            value={capitalInput}
            onChange={e => setCapitalInput(e.target.value)}
            placeholder={`Amount (${cur})`}
            style={{ width: '100%', marginBottom: '1rem' }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" style={{ flex: 1 }} onClick={handleSaveCapital} disabled={savingCapital}>
              {savingCapital ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setShowCapitalModal(false)} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
